import { isUsableTimeZone } from "~/lib/utils/timeZone";

/**
 * Display helpers for the driver-facing event surfaces (the nearby strip on
 * `/parking` and the `/events` browse list).
 *
 * Ported from the mobile app's `~/lib/format` so the two clients label the same
 * event identically — a driver who sees "Aug 14 – 16" in the app must not see
 * "Aug 14 – Aug 16" on the web. Keep the two in step when either changes.
 *
 * Every function takes the *venue's* IANA zone rather than reading the viewer's.
 * The events feed is nationwide: a 10:00 PM Pacific show renders as the next
 * day for a driver in New York, and an unlabelled "7:00 PM" doesn't say whose
 * 7:00 PM it is. `undefined` falls back to the viewer's zone — wrong for
 * out-of-state events, but the times still render rather than the row blanking.
 */

/**
 * The venue's IANA zone, or `undefined` when the event carries one this runtime
 * can't use.
 *
 * Validated rather than trusted for one reason that matters: `Intl.DateTimeFormat`
 * throws a `RangeError` on an unusable zone, and these formatters run during
 * render for every row — so a single bad value would blank the whole list rather
 * than mislabel one time. The field is also absent from responses served by a
 * backend older than it, which this app reaches against staging and local.
 */
export function eventTimeZone(event: {
  timezone?: string | null;
}): string | undefined {
  const timeZone = event.timezone;
  if (timeZone == null || timeZone.trim().length === 0) return undefined;
  return isUsableTimeZone(timeZone) ? timeZone : undefined;
}

/**
 * Formatters, cached per zone and shape.
 *
 * Constructing an `Intl.DateTimeFormat` is expensive, and this module is the
 * hot path for `/events`: that page holds every event day in memory and filters
 * on each keystroke, so the whole visible list re-renders — and one row builds
 * about eight of these between `formatEventWindow` and its helpers. Measured on
 * a desktop V8, 500 rows cost ~79ms of formatter construction uncached against
 * ~5ms cached, and a mid-range phone is several times slower again.
 *
 * Same approach, for the same reason, as `zoneFormatterCache` in
 * `~/lib/availability/setup`.
 *
 * `undefined` keys distinctly from any named zone: it means "the viewer's",
 * which is the documented fallback for an event carrying no usable zone.
 */
const formatterCache = new Map<string, Intl.DateTimeFormat>();

function formatter(
  shape: string,
  timeZone: string | undefined,
  options: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat {
  const key = `${shape}|${timeZone ?? ""}`;
  let cached = formatterCache.get(key);
  if (!cached) {
    cached = new Intl.DateTimeFormat("en-US", { timeZone, ...options });
    formatterCache.set(key, cached);
  }
  return cached;
}

/** Calendar fields of an instant *in a given zone*. */
function zonedParts(
  d: Date,
  timeZone: string | undefined,
): { year: string; month: string; day: string } {
  const parts = formatter("parts", timeZone, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(d);
  const value = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? "";
  return { year: value("year"), month: value("month"), day: value("day") };
}

function isSameZonedDay(
  a: Date,
  b: Date,
  timeZone: string | undefined,
): boolean {
  const first = zonedParts(a, timeZone);
  const second = zonedParts(b, timeZone);
  return (
    first.year === second.year &&
    first.month === second.month &&
    first.day === second.day
  );
}

function eventDate(d: Date, timeZone: string | undefined): string {
  return formatter("date", timeZone, {
    month: "short",
    day: "numeric",
  }).format(d);
}

function eventTime(d: Date, timeZone: string | undefined): string {
  return formatter("time", timeZone, {
    hour: "numeric",
    minute: "2-digit",
  }).format(d);
}

/** Short zone label for an instant, e.g. `"EDT"`, `"PST"`, `"GMT+5:30"`. */
function zoneAbbreviation(d: Date, timeZone: string | undefined): string {
  return (
    formatter("zone", timeZone, { timeZoneName: "short" })
      .formatToParts(d)
      .find((part) => part.type === "timeZoneName")?.value ?? ""
  );
}

/**
 * Both clock times of a window, with the zone named once — after the end time,
 * because both instants normally share one. Across a DST change they genuinely
 * differ and each side is labelled ("1:00 AM EDT – 1:30 AM EST").
 */
function zonedTimes(
  start: Date,
  end: Date,
  timeZone: string | undefined,
): { start: string; end: string } {
  const startZone = zoneAbbreviation(start, timeZone);
  const endZone = zoneAbbreviation(end, timeZone);
  const startTime = eventTime(start, timeZone);
  const endTime = eventTime(end, timeZone);
  return {
    start: startZone === endZone ? startTime : `${startTime} ${startZone}`,
    end: endZone ? `${endTime} ${endZone}` : endTime,
  };
}

/**
 * An event day's date, with no times — it has to fit the date pill on a card.
 *
 * Same day  → "Aug 9"
 * Spanning  → "Aug 7 – 9" (or "Aug 30 – Sep 2" across a month boundary)
 */
export function formatEventDateRange(
  startsAt: string,
  endsAt: string,
  timeZone?: string,
): string {
  const start = new Date(startsAt);
  const end = new Date(endsAt);
  const startStr = eventDate(start, timeZone);

  if (isSameZonedDay(start, end, timeZone)) return startStr;

  const startParts = zonedParts(start, timeZone);
  const endParts = zonedParts(end, timeZone);
  const sameMonth =
    startParts.year === endParts.year && startParts.month === endParts.month;
  const endStr = sameMonth
    ? String(Number(endParts.day))
    : eventDate(end, timeZone);
  return `${startStr} – ${endStr}`;
}

/**
 * Just the start, zone named — "7:15 PM PDT".
 *
 * For the hero strip, where a tile is one line wide and the full range doesn't
 * fit. The zone is still named: on a nationwide feed an unlabelled "7:15 PM"
 * doesn't say whose.
 */
export function formatEventStart(startsAt: string, timeZone?: string): string {
  const start = new Date(startsAt);
  const zone = zoneAbbreviation(start, timeZone);
  const time = eventTime(start, timeZone);
  return zone ? `${time} ${zone}` : time;
}

/** Clock times only, e.g. "7:15 PM – 10:30 PM PDT". Pair with the date pill. */
export function formatEventTimeRange(
  startsAt: string,
  endsAt: string,
  timeZone?: string,
): string {
  const times = zonedTimes(new Date(startsAt), new Date(endsAt), timeZone);
  return `${times.start} – ${times.end}`;
}

/**
 * The full window — dates and both times — for a surface with a line to spare.
 *
 * Same day  → "Aug 9, 7:15 PM – 10:30 PM PDT"
 * Spanning  → "Aug 7, 8:00 AM – Aug 9, 10:00 PM PDT"
 */
export function formatEventWindow(
  startsAt: string,
  endsAt: string,
  timeZone?: string,
): string {
  const start = new Date(startsAt);
  const end = new Date(endsAt);
  const startDate = eventDate(start, timeZone);
  const times = zonedTimes(start, end, timeZone);

  if (isSameZonedDay(start, end, timeZone)) {
    return `${startDate}, ${times.start} – ${times.end}`;
  }
  return `${startDate}, ${times.start} – ${eventDate(end, timeZone)}, ${times.end}`;
}

/**
 * "Golden Gate Park, San Francisco" — falls back to whichever of venue/city is
 * set, and to `null` when neither is (both are nullable on the backend).
 */
export function formatEventLocation(event: {
  venueName: string | null;
  city: string | null;
}): string | null {
  const parts = [event.venueName, event.city].filter(
    (part): part is string => !!part && part.trim().length > 0,
  );
  return parts.length > 0 ? parts.join(", ") : null;
}

/**
 * Whether the day is under way — the "Happening now" pill.
 *
 * Read off the occurrence's own window, never the event's run: on day 2 of a
 * festival the run has started but tonight's show has not.
 */
export function isHappeningNow(
  startsAt: string,
  endsAt: string,
  nowMs: number = Date.now(),
): boolean {
  const start = Date.parse(startsAt);
  const end = Date.parse(endsAt);
  if (Number.isNaN(start) || Number.isNaN(end)) return false;
  return start <= nowMs && end > nowMs;
}
